import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ChevronRight, Package, Check } from "lucide-react";
import { PRODUCT_CATEGORIES, CATALOG, WHATSAPP_LINK } from "../data/site";
import LeadForm from "../components/LeadForm";

const PLACEHOLDER_IMGS = {
  table:    "https://images.pexels.com/photos/32027903/pexels-photo-32027903.jpeg?auto=compress&cs=tinysrgb&w=800",
  bedding:  "https://images.pexels.com/photos/4153147/pexels-photo-4153147.jpeg?auto=compress&cs=tinysrgb&w=800",
  kids:     "https://images.pexels.com/photos/23600779/pexels-photo-23600779.jpeg?auto=compress&cs=tinysrgb&w=800",
  kitchen:  "https://images.pexels.com/photos/31591460/pexels-photo-31591460.jpeg?auto=compress&cs=tinysrgb&w=800",
  bath:     "https://images.pexels.com/photos/32795059/pexels-photo-32795059.jpeg?auto=compress&cs=tinysrgb&w=800",
  living:   "https://images.pexels.com/photos/36013228/pexels-photo-36013228.png?auto=compress&cs=tinysrgb&w=800",
  bags:     "https://images.pexels.com/photos/6850854/pexels-photo-6850854.jpeg?auto=compress&cs=tinysrgb&w=800",
  garments: "https://images.unsplash.com/photo-1761808070411-65ca8cdcdcfb?w=800&q=80",
  outdoor:  "https://images.unsplash.com/photo-1730129409193-dce14789ae88?w=800&q=80",
  fabric:   "https://images.unsplash.com/photo-1765994617009-362e42435ef6?w=800&q=80",
  gifting:  "https://images.pexels.com/photos/6694716/pexels-photo-6694716.jpeg?auto=compress&cs=tinysrgb&w=800",
};

function trackEvent(name, params = {}) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", name, params);
  }
}

export default function ProductCategoryPage() {
  const { slug } = useParams();
  const category = PRODUCT_CATEGORIES.find((c) => c.slug === slug);

  if (!category) {
    return (
      <main className="pt-40 pb-24 text-center">
        <p className="text-2xl text-[#1e2a5a] font-light">Category not found.</p>
        <Link to="/products" className="mt-6 inline-block text-[#b4462a] font-semibold underline">
          Back to all products
        </Link>
      </main>
    );
  }

  const products = CATALOG[category.key] || [];
  const idx = PRODUCT_CATEGORIES.findIndex((c) => c.key === category.key);
  const prev = PRODUCT_CATEGORIES[(idx - 1 + PRODUCT_CATEGORIES.length) % PRODUCT_CATEGORIES.length];
  const next = PRODUCT_CATEGORIES[(idx + 1) % PRODUCT_CATEGORIES.length];

  return (
    <>
      <Helmet>
        <title>{category.name} | Indus Art — Hand Block Printed Textiles from Jaipur</title>
        <meta name="description" content={`${category.tagline} Wholesale & export by Indus Art, Jaipur. 30+ countries served since 2007.`} />
        <meta property="og:title" content={`${category.name} | Indus Art`} />
        <meta property="og:description" content={category.tagline} />
        <meta property="og:url" content={`https://www.indusartjaipur.com/products/${category.slug}`} />
        <meta property="og:image" content={category.img} />
        <link rel="canonical" href={`https://www.indusartjaipur.com/products/${category.slug}`} />
      </Helmet>

      <main>
        {/* Breadcrumb */}
        <nav className="pt-28 pb-4 px-6 md:px-10 max-w-[1280px] mx-auto">
          <ol className="flex items-center gap-1.5 text-xs text-[#1a1a1a]/50 flex-wrap">
            <li><Link to="/" className="hover:text-[#1e2a5a] transition">Home</Link></li>
            <li><ChevronRight size={12} /></li>
            <li><Link to="/products" className="hover:text-[#1e2a5a] transition">Products</Link></li>
            <li><ChevronRight size={12} /></li>
            <li className="text-[#1e2a5a] font-semibold">{category.name}</li>
          </ol>
        </nav>

        {/* Hero */}
        <section className="bg-[#faf6ef]">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-12 md:py-20 grid md:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-4">
                Category {String(idx + 1).padStart(2, "0")}
              </div>
              <h1 className="text-4xl md:text-6xl font-light leading-[1.1] tracking-tight text-[#1e2a5a] mb-6">
                {category.name}
              </h1>
              <p className="text-lg text-[#1a1a1a]/70 leading-[1.8] mb-8 max-w-lg">
                {category.description}
              </p>
              {category.subcategories && (
                <div className="flex flex-wrap gap-2 mb-8">
                  {category.subcategories.map((s) => (
                    <span key={s} className="text-xs font-semibold bg-[#1e2a5a]/8 text-[#1e2a5a] rounded-full px-3 py-1.5">
                      {s}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/contact#lead-form"
                  onClick={() => trackEvent("cta_click", { location: "category_hero", category: category.key })}
                  className="btn-pill bg-[#1e2a5a] text-[#faf6ef] hover:bg-[#b4462a] py-4 px-7 text-sm shadow-cta"
                >
                  Request a Quote <ArrowRight size={15} strokeWidth={2.2} />
                </Link>
                <a
                  href={WHATSAPP_LINK(`Hi, I'm interested in your ${category.name} range`)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackEvent("whatsapp_click", { location: "category_hero", category: category.key })}
                  className="btn-pill bg-white border border-[#1e2a5a]/15 text-[#1e2a5a] hover:bg-[#d98f2f] hover:text-white hover:border-transparent py-4 px-7 text-sm"
                >
                  Chat on WhatsApp
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}
              className="rounded-3xl overflow-hidden aspect-[4/3] shadow-soft"
            >
              <img
                src={category.img}
                alt={category.name}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </section>

        {/* Product grid */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-2">Range</div>
                <h2 className="text-3xl md:text-4xl font-light text-[#1e2a5a]">
                  {products.length} products in this category
                </h2>
              </div>
              <p className="text-sm text-[#1a1a1a]/55 max-w-sm leading-[1.7]">
                All prices on request — we tailor quotes to your MOQ, design path, and market.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p, i) => (
                <ProductCard key={i} product={p} category={category} index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* Inline inquiry form */}
        <section id="category-inquiry" className="py-16 md:py-24 bg-[#faf6ef]">
          <div className="max-w-[900px] mx-auto px-6 md:px-10">
            <div className="text-center mb-10">
              <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-3">Get a Quote</div>
              <h2 className="text-3xl md:text-4xl font-light text-[#1e2a5a] mb-4">
                Enquire about {category.name}
              </h2>
              <p className="text-[#1a1a1a]/65 max-w-md mx-auto leading-[1.8]">
                Tell us your requirements — category, MOQ, target market, and design path. We respond within 24 hours.
              </p>
            </div>
            <LeadForm
              source={`category_page_${category.key}`}
              prefill={{ categories: [category.name], message: `I'm interested in your ${category.name} range. ` }}
            />
          </div>
        </section>

        {/* Category navigation */}
        <section className="py-12 bg-white border-t border-[#1a1a1a]/8">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 flex items-center justify-between flex-wrap gap-4">
            <Link
              to={`/products/${prev.slug}`}
              className="group flex items-center gap-3 text-sm font-semibold text-[#1e2a5a] hover:text-[#b4462a] transition"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-1 transition" />
              <span>
                <div className="text-xs text-[#1a1a1a]/40 font-normal mb-0.5">Previous</div>
                {prev.name}
              </span>
            </Link>
            <Link to="/products" className="text-xs font-bold tracking-[0.2em] uppercase text-[#1a1a1a]/40 hover:text-[#1e2a5a] transition">
              All Categories
            </Link>
            <Link
              to={`/products/${next.slug}`}
              className="group flex items-center gap-3 text-sm font-semibold text-[#1e2a5a] hover:text-[#b4462a] transition text-right"
            >
              <span>
                <div className="text-xs text-[#1a1a1a]/40 font-normal mb-0.5">Next</div>
                {next.name}
              </span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition" />
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}

function ProductCard({ product, category, index }) {
  const [inquired, setInquired] = useState(false);
  const img = PLACEHOLDER_IMGS[category.key];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.5, delay: index * 0.03 }}
      className="group rounded-2xl bg-[#faf6ef] border border-[#1a1a1a]/8 overflow-hidden hover:shadow-soft transition-all"
    >
      <div className="aspect-[4/3] overflow-hidden bg-[#e8e2d8]">
        <img
          src={img}
          alt={product.n}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="p-5">
        <h3 className="text-sm font-bold text-[#1e2a5a] leading-[1.4] mb-2">{product.n}</h3>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {product.specs.map((s) => (
            <span key={s} className="text-[10px] bg-white border border-[#1a1a1a]/10 text-[#1a1a1a]/60 rounded-full px-2.5 py-1">
              {s}
            </span>
          ))}
        </div>
        <button
          onClick={() => {
            setInquired(true);
            trackEvent("product_inquiry_click", { product: product.n, category: category.key });
          }}
          className={`w-full text-xs font-bold py-2.5 rounded-full transition-all ${
            inquired
              ? "bg-[#8a9a7b]/20 text-[#8a9a7b] cursor-default"
              : "bg-[#1e2a5a] text-[#faf6ef] hover:bg-[#b4462a] cursor-pointer"
          }`}
        >
          {inquired ? (
            <span className="flex items-center justify-center gap-1.5"><Check size={13} /> Enquiry noted</span>
          ) : (
            <span className="flex items-center justify-center gap-1.5"><Package size={13} /> Enquire / Request Sample</span>
          )}
        </button>
      </div>
    </motion.div>
  );
}
